/**
 * The grammar of a runtime condition: when a Widget shows, when a Form field is visible or disabled.
 *
 * Stored inside descriptors phis-server keeps, a Form's `visibleWhen` among them, so it is read here and
 * read once -- the renderer evaluates what the server accepted, by the same rules.
 */
import { readPhiControllerSignalAddress, readPhiSignalAddress, } from "./signals.js";
import { isPhiRecord, readPhiDotPath } from "./record.js";
/**
 * Where a condition reads the value it judges.
 *
 * `row`, `form`, `controller` and `widget` are values the page produced: a record under the cursor, what
 * has been typed, what a Controller holds, what a neighbouring Widget found out. `feature` is what this
 * Site was configured to have -- a named fact a Module publishes about its own setup, such as whether a
 * sign-in method is switched on, settled before anything renders and never seen by the browser. `page`
 * is the one the visitor arrived with --
 * the address of the page they opened, which is state as much as any of the others and the only kind
 * that is already settled before the first Widget renders. A confirmation link carries its token there,
 * an external login returns with its outcome there, and a Widget that should appear only for one of
 * those can say so without a component of its own to read the query string.
 */
export const PHI_RUNTIME_CONDITION_SOURCES = ["row", "form", "controller", "page", "widget", "feature"];
export const PHI_RUNTIME_CONDITION_OPERATORS = ["truthy", "falsy", "equals", "contains"];
export const PHI_RUNTIME_CONDITION_GROUP_MATCHES = ["all", "any"];
function readConditionValue(value) {
    return typeof value === "string" ? value : undefined;
}
function readConditionSource(value) {
    return PHI_RUNTIME_CONDITION_SOURCES.includes(value)
        ? value
        : null;
}
export function readPhiRuntimeValueCondition(value) {
    if (!isPhiRecord(value))
        return null;
    const source = readConditionSource(value.source);
    const operator = value.operator === "truthy" || value.operator === "falsy" ||
        value.operator === "equals" || value.operator === "contains"
        ? value.operator
        : null;
    const valuePath = typeof value.valuePath === "string" ? value.valuePath.trim() : "";
    const whenUnavailable = value.whenUnavailable === "matched" || value.whenUnavailable === "not-matched"
        ? value.whenUnavailable
        : undefined;
    const controllerAddress = source === "controller"
        ? readPhiControllerSignalAddress(value.controllerAddress)
        : undefined;
    const widgetAddress = source === "widget"
        ? readPhiSignalAddress(value.widgetAddress)
        : undefined;
    const conditionValue = readConditionValue(value.value);
    if (!source ||
        !operator ||
        !valuePath ||
        (source === "controller" && !controllerAddress) ||
        (source === "widget" && !widgetAddress) ||
        (operator !== "truthy" && operator !== "falsy" && conditionValue === undefined)) {
        return null;
    }
    return {
        source,
        controllerAddress,
        widgetAddress,
        valuePath,
        operator,
        whenUnavailable,
        value: operator === "truthy" || operator === "falsy" ? undefined : conditionValue,
        reason: typeof value.reason === "string" && value.reason.trim() ? value.reason.trim() : undefined,
    };
}
export function readPhiRuntimeConditionExpression(value) {
    const leaf = readPhiRuntimeValueCondition(value);
    if (leaf)
        return leaf;
    if (!isPhiRecord(value) || (value.match !== "all" && value.match !== "any") || !Array.isArray(value.conditions)) {
        return null;
    }
    const conditions = value.conditions.map(readPhiRuntimeConditionExpression);
    if (conditions.length === 0 || conditions.some((condition) => condition == null)) {
        return null;
    }
    return {
        match: value.match,
        conditions: conditions,
    };
}
export function collectPhiRuntimeValueConditions(expression) {
    if (!expression)
        return [];
    if ("source" in expression)
        return [expression];
    return expression.conditions.flatMap(collectPhiRuntimeValueConditions);
}
export function combinePhiRuntimeConditionExpressions(match, expressions) {
    const conditions = expressions.filter((entry) => Boolean(entry));
    if (conditions.length === 0)
        return undefined;
    if (conditions.length === 1)
        return conditions[0];
    return { match, conditions };
}
export function readPhiRuntimeConditionStateSignalValue(value) {
    return isPhiRecord(value) && isPhiRecord(value.state) ? { state: value.state } : null;
}
export function readPhiRuntimeConditionValue(input, valuePath) {
    return readPhiDotPath(input, valuePath);
}
function resolvePhiRuntimeConditionSource(condition, sources) {
    if (condition.source === "row")
        return sources.row ?? null;
    if (condition.source === "form")
        return sources.form ?? null;
    if (condition.source === "page")
        return sources.page ?? null;
    if (condition.source === "feature")
        return sources.features ?? null;
    if (condition.source === "widget") {
        return condition.widgetAddress ? sources.widgets?.[condition.widgetAddress] ?? null : null;
    }
    return condition.controllerAddress
        ? sources.controllers?.[condition.controllerAddress] ?? null
        : null;
}
export function matchesPhiRuntimeValueCondition(condition, sources) {
    const source = resolvePhiRuntimeConditionSource(condition, sources);
    if (source == null)
        return false;
    const current = readPhiRuntimeConditionValue(source, condition.valuePath);
    if ((condition.source === "controller" || condition.source === "widget") && current === undefined)
        return false;
    if (condition.operator === "truthy")
        return Boolean(current);
    if (condition.operator === "falsy")
        return !current;
    if (condition.operator === "equals")
        return Object.is(current, condition.value);
    return Array.isArray(current)
        ? current.some((entry) => typeof entry === "string" && entry === condition.value)
        : typeof current === "string" && typeof condition.value === "string"
            ? current.includes(condition.value)
            : false;
}
export function evaluatePhiRuntimeConditionExpression(expression, sources) {
    if ("source" in expression) {
        const source = resolvePhiRuntimeConditionSource(expression, sources);
        if (source == null)
            return expression.whenUnavailable ?? "unavailable";
        if ((expression.source === "controller" || expression.source === "widget") &&
            readPhiRuntimeConditionValue(source, expression.valuePath) === undefined) {
            return expression.whenUnavailable ?? "unavailable";
        }
        return matchesPhiRuntimeValueCondition(expression, sources) ? "matched" : "not-matched";
    }
    const results = expression.conditions.map((condition) => evaluatePhiRuntimeConditionExpression(condition, sources));
    if (expression.match === "all") {
        if (results.some((result) => result === "not-matched"))
            return "not-matched";
        return results.every((result) => result === "matched") ? "matched" : "unavailable";
    }
    if (results.some((result) => result === "matched"))
        return "matched";
    return results.every((result) => result === "not-matched") ? "not-matched" : "unavailable";
}
